import { ElementRef } from '@angular/core';

/** @docs-private */
export type Constructor<T> = new (...args: any[]) => T;

/**
 * This is a permissive type for abstract class constructors.
 * @docs-private
 */
export type AbstractConstructor<T = object> = abstract new (...args: any[]) => T;

/** @docs-private */
export interface CanSize {
    /** Theme size palette for the component. */
    size: SizeVariation;

    /** Default size to fall back to if no value is set. */
    defaultSize: SizeVariation | undefined;
}

/** @docs-private */
export type CanSizeCtor = Constructor<CanSize>;

/** @docs-private */
export interface HasElementRef {
    _elementRef: ElementRef;
}

/** Possible size palette values. */
export type SizeVariation = 'large' | 'medium' | 'small' | 'full' | undefined;

/** Mixin to augment a directive with a `size` property. */
export function mixinSize<T extends Constructor<HasElementRef>>(base: T, defaultSize?: SizeVariation): CanSizeCtor & T {
    return class extends base {
        private _size: SizeVariation;
        defaultSize = defaultSize;

        get size(): SizeVariation {
            return this._size;
        }
        set size(value: SizeVariation) {
            const sizeVariation = value || this.defaultSize;

            if (sizeVariation !== this._size) {
                if (this._size) {
                    this._elementRef.nativeElement.classList.remove(`aum-size-${this._size}`);
                }
                if (sizeVariation) {
                    this._elementRef.nativeElement.classList.add(`aum-size-${sizeVariation}`);
                }

                this._size = sizeVariation;
            }
        }

        constructor(...args: any[]) {
            super(...args);

            // Set the default size that can be specified from the mixin.
            this.size = defaultSize;
        }
    };
}
